// Meal planner + grocery list engine. Links every planned day to a real recipe.
import { RECIPES } from "../data/recipes";
import { COLLECTIONS } from "../data/collections";

export const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const PLAN_KEY = "cot.mealplan.v2";
const GROCERY_KEY = "cot.grocery.v1";

// Shopping-ready ingredients for the free recipes (recipe text is too wordy for a list).
const FREE_GROCERIES = {
  "cheese-on-toast": [["Bread", "Bakery"], ["Cheese", "Dairy"], ["Butter", "Dairy"]],
  "boiled-rice": [["Rice", "Cupboard"]],
  "fried-rice": [["Rice", "Cupboard"], ["Eggs", "Dairy"], ["Spring onions", "Fresh"], ["Frozen peas", "Frozen"], ["Soy sauce", "Cupboard"], ["Oil", "Cupboard"]],
  "sushi-rolls": [["Sushi rice", "Cupboard"], ["Nori sheets", "Cupboard"], ["Cucumber", "Fresh"], ["Avocado", "Fresh"], ["Tin of tuna", "Cupboard"], ["Soy sauce", "Cupboard"]],
  "scrambled-eggs": [["Eggs", "Dairy"], ["Butter", "Dairy"], ["Milk", "Dairy"], ["Bread", "Bakery"]],
  "simple-pasta": [["Pasta", "Cupboard"], ["Tin chopped tomatoes", "Cupboard"], ["Garlic", "Fresh"], ["Olive oil", "Cupboard"], ["Cheese", "Dairy"]],
  "beans-on-toast": [["Tin baked beans", "Cupboard"], ["Bread", "Bakery"], ["Butter", "Dairy"], ["Cheese", "Dairy"]],
};

const toItems = (pairs) => pairs.map(([text, aisle]) => ({ text, aisle }));

export const CATALOG = [
  ...RECIPES.filter((r) => FREE_GROCERIES[r.slug]).map((r) => ({
    id: r.slug, title: r.shortTitle || r.title, group: "Free recipes",
    path: `/recipe/${r.slug}`, ingredients: toItems(FREE_GROCERIES[r.slug]),
  })),
  ...COLLECTIONS.flatMap((c) => c.recipes
    .filter((r) => r.ingredients && !r.listOnly)
    .map((r) => ({
      id: `${c.slug}/${r.slug}`, title: r.title, group: c.shortTitle || c.title,
      path: `/collections/${c.slug}#${r.slug}`, ingredients: toItems(r.ingredients),
    }))),
];

export const getMeal = (id) => CATALOG.find((m) => m.id === id);
export const CATALOG_GROUPS = [...new Set(CATALOG.map((m) => m.group))];

export const DEFAULT_PLAN = { Mon: "cheese-on-toast", Tue: "moving-out/one-pan-mince", Wed: "beans-on-toast", Thu: "air-fryer/af-chicken-thighs", Fri: "moving-out/tuna-pasta-bake", Sat: "moving-out/midweek-stir-fry", Sun: "simple-pasta" };

const read = (key, fallback) => {
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : fallback; } catch { return fallback; }
};
const write = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage full / private mode */ }
};

export const loadPlan = () => ({ ...Object.fromEntries(DAYS.map((d) => [d, ""])), ...read(PLAN_KEY, DEFAULT_PLAN) });
export const savePlan = (plan) => write(PLAN_KEY, plan);

// Puts a meal on a given day. Returns the meal it replaced (if any).
export const addMealToPlan = (id, day) => {
  const plan = loadPlan();
  const replaced = plan[day] && plan[day] !== id ? getMeal(plan[day]) : null;
  savePlan({ ...plan, [day]: id });
  return replaced;
};

export const loadGrocery = () => read(GROCERY_KEY, []);
export const saveGrocery = (items) => write(GROCERY_KEY, items);

const keyOf = (text) => text.trim().toLowerCase();

// Merge ingredients into the list without duplicates. Returns how many were new.
export const addToGrocery = (ingredients, forLabel, source = "added") => {
  const items = loadGrocery();
  let added = 0;
  ingredients.forEach(({ text, aisle }) => {
    const existing = items.find((i) => keyOf(i.text) === keyOf(text));
    if (existing) {
      if (forLabel && !(existing.for || []).includes(forLabel)) existing.for = [...(existing.for || []), forLabel];
      return;
    }
    items.push({ id: `${keyOf(text).replace(/[^a-z0-9]+/g, "-")}-${Date.now()}-${added}`, text, aisle, checked: false, source, for: forLabel ? [forLabel] : [] });
    added += 1;
  });
  saveGrocery(items);
  return added;
};

// Rebuild the auto items from the week's plan; manual items and ticks are kept.
export const generateFromPlan = (plan) => {
  const prev = loadGrocery();
  const manual = prev.filter((i) => i.source !== "auto");
  const ticked = new Set(prev.filter((i) => i.checked).map((i) => keyOf(i.text)));
  saveGrocery(manual);
  DAYS.forEach((d) => { const m = getMeal(plan[d]); if (m) addToGrocery(m.ingredients, d, "auto"); });
  const next = loadGrocery().map((i) => (ticked.has(keyOf(i.text)) ? { ...i, checked: true } : i));
  saveGrocery(next);
  return next;
};
